/* eslint-disable no-console */
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import type { DependencyInitializerFunction, IConfig, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ApplicationInsights, DistributedTracingModes, SeverityLevel } from "@microsoft/applicationinsights-web";
import { GridGetRowsError } from "@mui/x-data-grid-premium";
import axios, { AxiosError } from "axios";
import { clientCodeHeader, getClientCode } from "./api/clientApiContext";
import type { ApiError } from "./api/types";
import { getSharedVariables } from "./variables";

const ignoredRequestPredicates: DependencyInitializerFunction[] = [
  (details) => details.aborted ?? false, // Aborted requests
  (details) => details.item.responseCode === 0, // Network errors
];

const dependencyInitializerHandler: DependencyInitializerFunction = (details) => {
  if (ignoredRequestPredicates.some((predicate) => predicate(details))) {
    return false;
  }

  return undefined;
};

interface IExceptionData {
  properties?: Record<string, string>;
  message?: string;
  exceptions?: Array<{ message?: string; typeName?: string; stack?: string }>;
}

const ignoredExceptionMessages = [
  "ResizeObserver loop",
  "ErrorEvent: Script error", // Safari's CORS-sanitized window.onerror message; no stack/file/line, not actionable
  "Failed to complete negotiation with the server: TypeError: Failed to fetch", // Transient SignalR initial negotiation failure; the next connection attempt can recover
];

const matchesIgnoredExceptionMessage = (message: string | undefined): boolean => {
  if (!message) {
    return false;
  }

  return ignoredExceptionMessages.some((ignoredMessage) => message.includes(ignoredMessage));
};

const ignoredStackSources = ["chrome-extension://", "moz-extension://", "safari-web-extension://"];

const hasIgnoredStackSource = (baseData: IExceptionData): boolean =>
  baseData.exceptions?.some((exception) =>
    ignoredStackSources.some((source) => exception.stack?.includes(source) ?? false)
  ) ?? false;

const shouldIgnoreException = (baseData: IExceptionData): boolean => {
  if (matchesIgnoredExceptionMessage(baseData.message)) {
    return true;
  }

  if (hasIgnoredStackSource(baseData)) {
    return true;
  }

  return baseData.exceptions?.some((exception) => matchesIgnoredExceptionMessage(exception.message)) ?? false;
};

const MAX_EVENTS_PER_SIGNATURE = 5;
const MAX_TRACKED_SIGNATURES = 500;
const exceptionSignatureCounts = new Map<string, number>();

const getExceptionSignature = (baseData: IExceptionData): string | undefined => {
  const firstException = baseData.exceptions?.[0];
  const typeName = firstException?.typeName ?? "";
  const message = firstException?.message ?? baseData.message ?? "";
  if (!typeName && !message) {
    return undefined;
  }
  return `${typeName}|${message}`;
};

const isExceptionThrottled = (baseData: IExceptionData): boolean => {
  const signature = getExceptionSignature(baseData);
  if (signature === undefined) {
    return false;
  }
  if (!exceptionSignatureCounts.has(signature) && exceptionSignatureCounts.size >= MAX_TRACKED_SIGNATURES) {
    exceptionSignatureCounts.clear();
  }
  const count = exceptionSignatureCounts.get(signature) ?? 0;
  exceptionSignatureCounts.set(signature, count + 1);
  return count >= MAX_EVENTS_PER_SIGNATURE;
};

const telemetryInitializerHandler = (item: ITelemetryItem): boolean | void => {
  if (item.baseType === "ExceptionData") {
    const baseData = item.baseData as IExceptionData;
    if (shouldIgnoreException(baseData)) {
      return false;
    }

    if (isExceptionThrottled(baseData)) {
      return false;
    }

    if (baseData?.properties) {
      baseData.properties = {
        ...baseData.properties,
        browserUrl: window.location.href,
        clientCode: getClientCode(),
      };
    }
  }
};

let appInsights: ApplicationInsights | undefined = undefined;

const reportedCspViolations = new Set<string>();

const withoutQueryAndFragment = (url: string): string => url.replace(/[?#].*/, "");

// blockedURI carries only the scheme ("chrome-extension") for extension resources, so match the scheme, not "://".
const browserExtensionUrlPattern = /^[a-z-]*extension(:|$)/i;

const cspViolationHandler = (event: SecurityPolicyViolationEvent) => {
  const isFromBrowserExtension =
    browserExtensionUrlPattern.test(event.blockedURI) || browserExtensionUrlPattern.test(event.sourceFile);
  if (isFromBrowserExtension) {
    return;
  }

  const blockedUri = withoutQueryAndFragment(event.blockedURI);
  const sourceFile = withoutQueryAndFragment(event.sourceFile);
  const violationKey = [
    event.disposition,
    event.effectiveDirective,
    blockedUri,
    sourceFile,
    event.lineNumber,
    event.columnNumber,
  ].join("|");
  if (reportedCspViolations.has(violationKey)) {
    return;
  }
  reportedCspViolations.add(violationKey);

  trackEvent("CspViolation", {
    disposition: event.disposition,
    effectiveDirective: event.effectiveDirective,
    blockedUri,
    sourceFile,
    lineNumber: event.lineNumber,
    columnNumber: event.columnNumber,
    sample: event.sample,
    documentUri: withoutQueryAndFragment(event.documentURI),
  });
};

export const appInsightsReactPlugin = new ReactPlugin();

// "bot[/-]" matches "Googlebot/2.1" or "AdsBot-Google" but not phone models such as "CUBOT P30".
const botUserAgentPattern = /bot[/-]|crawler|spider|HeadlessChrome/i;

const isAutomatedBrowser = (): boolean => navigator.webdriver || botUserAgentPattern.test(navigator.userAgent);

export const initAppInsights = (config: IConfig) => {
  if (appInsights || isAutomatedBrowser()) {
    return;
  }

  const appInsightsConnString = getSharedVariables().APP_INSIGHTS_CONN_STRING;

  if (!appInsightsConnString) {
    console.error("Missing app insights connection string");
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      ...config,
      addRequestContext: () => ({ ...clientCodeHeader() }),
      connectionString: appInsightsConnString,
      enableAutoRouteTracking: true,
      extensions: [appInsightsReactPlugin],
      // Configure distributed tracing to use both AI and W3C standards
      distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
      // Enable tracking of AJAX/fetch dependencies
      disableFetchTracking: false,
      // Enable cross-origin correlation
      enableCorsCorrelation: true,
      extensionConfig: {
        ["AppInsightsCfgSyncPlugin"]: {
          cfgUrl: "", // Prevent loading config from default CDN (js.monitor.azure.com/scripts/b/ai.config.1.cfg.json)
        },
      },
    },
  });

  appInsights.addDependencyInitializer(dependencyInitializerHandler);

  appInsights.addTelemetryInitializer(telemetryInitializerHandler);

  try {
    appInsights.loadAppInsights();
  } catch (error) {
    console.error("Failed to load app insights", error);
  }

  document.addEventListener("securitypolicyviolation", cspViolationHandler);
};

// For an app that creates its own ApplicationInsights instance; the shared log and track helpers then report to it.
export const attachAppInsights = (instance: ApplicationInsights) => {
  appInsights ??= instance;
};

const getErrorInstance = (error: unknown): Error => {
  if (!error) {
    return new Error("Unknown Error");
  }

  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error(JSON.stringify(error));
};

const excludeErrorFromTracking = (error: unknown) => {
  if (!error) {
    return false;
  }

  if (axios.isAxiosError(error)) {
    return (
      error.code === AxiosError.ERR_NETWORK ||
      error.code === AxiosError.ECONNABORTED ||
      error.code === AxiosError.ERR_CANCELED ||
      axios.isCancel(error)
    );
  }

  if (typeof error === "object" && error !== null) {
    const apiError = error as ApiError;
    return apiError.isCanceledRequest || apiError.code === 0 || apiError.code === 401 || apiError.code === 403;
  }

  return false;
};

type ErrorLogProperties = Record<string, string | number | boolean>;

const buildErrorLogProperties = (context: string, properties?: ErrorLogProperties): ErrorLogProperties => {
  const result: ErrorLogProperties = { context };

  Object.entries(properties ?? {}).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
  });

  return result;
};

export const getCurrentOperationId = (): string | undefined => appInsights?.getTraceCtx()?.getTraceId();

export const logInfo = (message: string, context?: string) => {
  const prefix = context ? `[${context}] ` : "";

  if (appInsights) {
    appInsights.trackTrace({ message: prefix + message }, { severity: SeverityLevel.Information });
  }
};

interface TelemetryProperties {
  [key: string]: string | number | boolean;
}

export const trackMetric = (name: string, value: number, properties?: TelemetryProperties): void => {
  if (!appInsights) {
    return;
  }
  appInsights.trackMetric({ name, average: value }, properties);
};

export const trackEvent = (name: string, properties?: TelemetryProperties): void => {
  if (!appInsights) {
    return;
  }
  appInsights.trackEvent({ name }, properties);
};

export const logError = (error: unknown, context: string, properties?: ErrorLogProperties) => {
  if (properties) {
    console.error(context, error, properties);
  } else {
    console.error(context, error);
  }

  if (!appInsights) {
    return;
  }

  if (excludeErrorFromTracking(error)) {
    return;
  }

  if (error instanceof GridGetRowsError && excludeErrorFromTracking(error.cause)) {
    return;
  }

  appInsights.trackException({ exception: getErrorInstance(error) }, buildErrorLogProperties(context, properties));
};
